import { useEffect, useState } from 'react';
import { Mail, Trash2, Calendar } from 'lucide-react';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/Toast';

const AdminContacts = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedContact, setSelectedContact] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, contact: null });
    const [toast, setToast] = useState(null);

    useEffect(() => {
        // For now, show empty state since we don't have contact API implemented yet
        setLoading(false);
        setContacts([]);
    }, []);

    const handleDelete = async () => {
        try {
            // await contactAPI.delete(deleteDialog.contact.id);
            setContacts(contacts.filter(c => c.id !== deleteDialog.contact.id));
            showToast('Contact deleted successfully', 'success');
            setDeleteDialog({ isOpen: false, contact: null });
            if (selectedContact?.id === deleteDialog.contact.id) {
                setSelectedContact(null);
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
            showToast('Failed to delete contact', 'error');
        }
    };

    const showToast = (message, type) => {
        setToast({ message, type });
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Contact Submissions</h1>
                <p className="text-gray-600 mt-1">View and manage contact form submissions</p>
            </div>

            {/* Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Contacts List */}
                <div className="lg:col-span-1 bg-white rounded-lg shadow">
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="font-semibold text-gray-900">Messages ({contacts.length})</h2>
                    </div>
                    <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-earth-green"></div>
                            </div>
                        ) : contacts.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <Mail className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                <p>No contact submissions yet</p>
                            </div>
                        ) : (
                            contacts.map((contact) => (
                                <div
                                    key={contact.id}
                                    onClick={() => setSelectedContact(contact)}
                                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedContact?.id === contact.id ? 'bg-blue-50' : ''
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-medium text-gray-900">{contact.name}</h3>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteDialog({ isOpen: true, contact });
                                            }}
                                            className="text-red-600 hover:text-red-900"
                                            title="Delete"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-1">{contact.email}</p>
                                    {contact.subject && (
                                        <p className="text-sm text-gray-700 font-medium mb-1">{contact.subject}</p>
                                    )}
                                    <p className="text-xs text-gray-500 flex items-center">
                                        <Calendar className="h-3 w-3 mr-1" />
                                        {formatDate(contact.createdAt)}
                                    </p>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Message Detail */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow">
                    {selectedContact ? (
                        <div className="p-6">
                            <div className="border-b border-gray-200 pb-4 mb-4">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    {selectedContact.subject || 'No Subject'}
                                </h2>
                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                    <div>
                                        <span className="font-medium">From:</span> {selectedContact.name}
                                    </div>
                                    <div>
                                        <span className="font-medium">Email:</span> {selectedContact.email}
                                    </div>
                                    <div>
                                        <span className="font-medium">Date:</span> {formatDate(selectedContact.createdAt)}
                                    </div>
                                </div>
                            </div>
                            <div className="prose max-w-none">
                                <p className="text-gray-700 whitespace-pre-wrap">{selectedContact.message}</p>
                            </div>
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <button
                                    onClick={() => setDeleteDialog({ isOpen: true, contact: selectedContact })}
                                    className="btn-outline text-red-600 border-red-600 hover:bg-red-50 inline-flex items-center space-x-2"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    <span>Delete Message</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full min-h-[400px] text-gray-500">
                            <div className="text-center">
                                <Mail className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                <p>Select a message to view details</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteDialog.isOpen}
                title="Delete Contact"
                message={`Are you sure you want to delete the message from "${deleteDialog.contact?.name}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                onCancel={() => setDeleteDialog({ isOpen: false, contact: null })}
            />

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default AdminContacts;
